// Websites where staff can report a bug via Page Feedback guest access.
// Clicking a site opens it in a new tab with a write-only "guest" token in the
// URL, which activates the Page Feedback annotation widget so the reporter can
// draw on the page and submit — no login, routed straight to the web team
// (Page Feedback → n8n → Teams).
//
// These tokens are GUEST scope: write-only feedback submission, no dashboard
// access and no ability to read others' feedback. They live in reviewer_tokens
// on the Page Feedback server (kind='guest', non-expiring). Safe to ship in
// this internal, auth-gated portal.

export type WebsiteIssueSite = {
  name: string;
  /** Base URL the link opens — may include a landing path for sites that
   *  live under a subdirectory (e.g. …/help-center). */
  origin: string;
  token: string;
  description: string;
};

// Order here = display order in the picker.
export const WEBSITE_ISSUE_SITES: WebsiteIssueSite[] = [
  {
    name: 'Star Micronics',
    origin: 'https://starmicronics.com',
    token: 'j1B7FkFLAXY_tU_X5amknIAp47M2tTjj',
    description: 'starmicronics.com — main marketing site',
  },
  {
    name: 'Help Center',
    origin: 'https://starmicronics.com/help-center',
    token: 'RvpSQBpNgt3YYUm1MYozn-qy0OEXH6a6',
    description: 'starmicronics.com/help-center — knowledge base',
  },
  {
    name: 'Support',
    origin: 'https://starmicronics.com/support',
    token: 'bGTlbjjIIBVABJkPXanY6ynWqQj8lcb_',
    description: 'starmicronics.com/support — support site',
  },
  {
    name: 'Warehouse Portal',
    origin: 'https://warehouse.starmicronics.com',
    token: 'w_1ubNJbNCcY3zBt2hnvk5CFf-qHzFZp',
    description: 'warehouse.starmicronics.com — warehouse operations portal',
  },
  {
    name: 'AI Chat Review',
    origin: 'https://aichat.starmicronics.com',
    token: 'fYHiYGJjJC8js4C4pfZDp4NvPK1J-cDD',
    description: 'aichat.starmicronics.com — AI chat review dashboard',
  },
];

/** The guest-enable URL for a site: opens it with the Page Feedback widget
 *  activated. The token rides under both the new ?feedback= param and the
 *  legacy ?pagepen= param so the link works whether the target site runs a
 *  pre- or post-rename widget build (drop `pagepen` once all sites are on
 *  @ccnanas/page-feedback). The signed-in user's name prefills the widget's
 *  "Your name" field, and their email (pf_email, widget ≥0.5.0) rides along on
 *  submissions so the web team's automations can notify them when it's fixed. */
export function websiteIssueUrl(site: WebsiteIssueSite, name?: string, email?: string): string {
  const params = new URLSearchParams({ feedback: site.token, pagepen: site.token });
  if (name && name.trim()) params.set('pp_name', name.trim());
  if (email && email.trim()) params.set('pf_email', email.trim());
  return `${site.origin}/?${params.toString()}`;
}
